import React from 'react';

import './css/Traceability.css'

import { useCursorRimLight } from './rimLight'
import { useStagePlayer } from './stagePlayer'
import { Steps } from './Steps'


// How a copy is traced back to whoever it was given to, told in four steps
// that play on their own and can be stepped through by hand.
//
// The scene is one SVG. Every node is drawn once, centred on its own origin,
// and placed with a transform computed from a layout, so the same drawings
// serve the wide scene (left to right) and the narrow one (top to bottom).
// A step only toggles a class on the scene; the stylesheet does the rest —
// the copies fly, the paths draw themselves, the answer lights up. The one
// exception is the point that runs back along the return path, an SVG motion
// started from here when the last step begins.

type Stage = 1 | 2 | 3 | 4

interface Point {
	x: number
	y: number
}

interface Layout {
	width: number
	height: number
	///@brief   Centres of the three fixed drawings.
	server: Point
	///@brief   The server's label, above or below its card: the wide scene
	///         leaves by the card's side and can keep it underneath, the
	///         stacked one leaves by its bottom edge and would run the send
	///         paths and the copy straight through it.
	serverLabelY: number
	recipients: Point[]
	found: Point
	///@brief   Where the single marked image sits before it is copied.
	copyStart: Point
	///@brief   Where each copy sits once delivered, on its own send path.
	copyStops: Point[]
	///@brief   Where the leaked copy sits, inside the browser frame.
	copyLeak: Point
	///@brief   Server to each recipient.
	sendPaths: string[]
	///@brief   The leaked recipient to the browser frame.
	leakPath: string
	///@brief   The browser frame back to the server: the trace is followed
	///         back to its source, so the path starts at the copy. It goes
	///         round the scene, out of one side and into the other, so it
	///         crosses no label.
	returnPath: string
	///@brief   Centre of the answer chip.
	answer: Point
}

// The four captions under the scene: a title each, the scene says the rest.
const STEPS: string[] = [
	'An invisible mark',
	'One per fan',
	'A copy leaks',
	'Traced back',
]

const COPY_LETTERS: string[] = [ 'A', 'B', 'C' ]
///@brief   Index of the recipient whose copy leaks.
const LEAKED: number = 1

///@brief   How long each step stays on screen before the next one plays.
///         The last one holds longer: the answer arrives two seconds in.
const STAGE_DURATIONS_MS: number[] = [ 2800, 3200, 3200, 5200 ]
///@brief   The scene fades out for this long before jumping back to the
///         first step, so the copies never fly backwards across it.
const WRAP_FADE_MS: number = 380

///@brief   Below this width the scene stacks top to bottom.
const STACKED_QUERY: string = '(max-width: 640px)'

// A cubic whose two control points share the middle abscissa (or ordinate)
// passes through this point at t = 0.8; the copies park there, on the line.
const STOP_T: number = 0.8


function cubicAt( t: number, p0: number, p1: number, p2: number, p3: number ): number {
	const u: number = 1 - t
	return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3
}


///@brief   Left to right: the server, the three recipients in a column, the
///         browser frame; the trace comes back underneath.
function wideLayout(): Layout {
	const recipients: Point[] = [ { x: 500, y: 95 }, { x: 500, y: 200 }, { x: 500, y: 305 } ]
	// From the server card's right edge to each recipient's left edge.
	const x0: number = 195
	const x1: number = 470
	const xm: number = ( x0 + x1 ) / 2

	return {
		width: 960,
		height: 420,
		server: { x: 120, y: 200 },
		serverLabelY: 76,
		recipients,
		found: { x: 830, y: 200 },
		copyStart: { x: 250, y: 200 },
		copyStops: recipients.map( ( p: Point ): Point => ( {
			x: cubicAt( STOP_T, x0, xm, xm, x1 ),
			y: cubicAt( STOP_T, 200, 200, p.y, p.y ),
		} ) ),
		copyLeak: { x: 830, y: 208 },
		sendPaths: recipients.map( ( p: Point ): string => `M${ x0} 200 C ${ xm } 200, ${ xm } ${ p.y }, ${ x1 } ${ p.y }` ),
		leakPath: 'M532 200 H 745',
		returnPath: 'M915 200 H 938 V 386 H 25 V 200 H 45',
		answer: { x: 830, y: 322 },
	}
}


///@brief   Top to bottom, for phones: the recipients in a row in the middle,
///         the trace comes back up the right-hand side.
function stackedLayout(): Layout {
	const recipients: Point[] = [ { x: 95, y: 300 }, { x: 210, y: 300 }, { x: 325, y: 300 } ]
	// From the server card's bottom edge to each recipient's top edge.
	const y0: number = 144
	const y1: number = 272
	const ym: number = ( y0 + y1 ) / 2

	return {
		width: 420,
		height: 730,
		server: { x: 210, y: 96 },
		// Above the card: everything leaves the server downwards here.
		serverLabelY: -64,
		recipients,
		found: { x: 210, y: 560 },
		// Clear of the card, so the marked image reads as having come out of
		// it rather than as part of it.
		copyStart: { x: 210, y: 196 },
		copyStops: recipients.map( ( p: Point ): Point => ( {
			x: cubicAt( STOP_T, 210, 210, p.x, p.x ),
			y: cubicAt( STOP_T, y0, ym, ym, y1 ),
		} ) ),
		copyLeak: { x: 210, y: 566 },
		sendPaths: recipients.map( ( p: Point ): string => `M210 ${ y0 } C 210 ${ ym }, ${ p.x } ${ ym }, ${ p.x } ${ y1 }` ),
		leakPath: 'M210 332 V 495',
		returnPath: 'M297 560 H 385 V 96 H 287',
		answer: { x: 210, y: 690 },
	}
}


function place( p: Point ): React.CSSProperties {
	return { transform: `translate(${ p.x }px, ${ p.y }px)` }
}


///@brief   Where copy `i` sits at this step. The leaked one also tilts and
///         grows a little once reposted: it is no longer the file that left.
function copyStyle( layout: Layout, stage: Stage, i: number ): React.CSSProperties {
	if ( stage === 1 ) return place( layout.copyStart )
	if ( i === LEAKED && stage >= 3 ) {
		return { transform: `translate(${ layout.copyLeak.x }px, ${ layout.copyLeak.y }px) rotate(-6deg) scale(1.12)` }
	}
	return place( layout.copyStops[ i ] )
}


function ServerGlyph( { labelY }: { labelY: number } ): React.JSX.Element {
	const rows: number[] = [ -22, 0, 22 ]

	return (
		<>
			<rect className="Traceability-Card" x="-75" y="-48" width="150" height="96" rx="12" />
			{ rows.map( ( y: number ): React.JSX.Element => (
				<g key={ y } >
					<rect className="Traceability-Stroke" x="-45" y={ y - 8 } width="90" height="16" rx="5" />
					<circle className="Traceability-Fill" cx="-35" cy={ y } r="2" />
					<path className="Traceability-Stroke" d={ `M-22 ${ y } H 30` } />
				</g>
			) ) }
			<text className="Traceability-Label" y={ labelY } >Your server</text>
		</>
	)
}


function RecipientGlyph( { name }: { name: string } ): React.JSX.Element {
	return (
		<>
			<circle className="Traceability-Ring" r="37" />
			<circle className="Traceability-Card" r="30" />
			<circle className="Traceability-Stroke" cy="-6" r="6.5" />
			<path className="Traceability-Stroke" d="M-13 15 a13 13 0 0 1 26 0" />
			<text className="Traceability-Label" y="52" >{ name }</text>
		</>
	)
}


function FoundGlyph(): React.JSX.Element {
	return (
		<>
			<rect className="Traceability-Card" x="-85" y="-65" width="170" height="130" rx="12" />
			<path className="Traceability-Stroke-Faint" d="M-85 -41 H 85" />
			<circle className="Traceability-Fill" cx="-70" cy="-53" r="3" />
			<circle className="Traceability-Fill" cx="-59" cy="-53" r="3" />
			<circle className="Traceability-Fill" cx="-48" cy="-53" r="3" />
			<rect className="Traceability-Stroke-Faint" x="-34" y="-58" width="98" height="10" rx="5" />
			<text className="Traceability-Label" y="95" >Found online</text>
		</>
	)
}


function CopyGlyph( { letter }: { letter: string } ): React.JSX.Element {
	return (
		<>
			<rect className="Traceability-Photo" x="-28" y="-21" width="56" height="42" rx="6" />
			<circle className="Traceability-Photo-Sun" cx="-15" cy="-9" r="4" />
			<path className="Traceability-Photo-Hills" d="M-24 15 L-10 -1 L-2 8 L6 2 L24 15 Z" />
			<rect className="Traceability-Mark" x="-28" y="-21" width="56" height="42" rx="6" fill="url(#Traceability-Dots)" />
			<g className="Traceability-Chip" >
				<circle cx="22" cy="-15" r="9" />
				<text x="22" y="-11.5" >{ letter }</text>
			</g>
		</>
	)
}


export function Traceability(): React.JSX.Element {

	const player = useStagePlayer( STAGE_DURATIONS_MS, WRAP_FADE_MS )
	const { stage: step, bFading, bSnap, bReducedMotion, rootRef } = player
	const stage: Stage = step as Stage

	const [ bStacked, setStacked ] = React.useState<boolean>( (): boolean =>
		typeof window.matchMedia === 'function' && window.matchMedia( STACKED_QUERY ).matches
	)
	const motionRef = React.useRef<SVGElement>( null )

	const layout: Layout = React.useMemo( (): Layout => bStacked ? stackedLayout() : wideLayout(), [ bStacked ] )

	useCursorRimLight( rootRef )

	// Wide or stacked, following the viewport.
	React.useEffect( (): ( () => void ) => {
		if ( typeof window.matchMedia !== 'function' ) return (): void => {}
		const query: MediaQueryList = window.matchMedia( STACKED_QUERY )
		const update = (): void => setStacked( query.matches )
		query.addEventListener( 'change', update )
		return (): void => query.removeEventListener( 'change', update )
	}, [] )

	// The point runs back along the trace when the last step begins.
	React.useEffect( (): void => {
		if ( stage !== 4 || bReducedMotion ) return
		const motion: SVGElement | null = motionRef.current
		if ( motion !== null && 'beginElement' in motion ) {
			( motion as SVGAnimationElement ).beginElement()
		}
	}, [ stage, bReducedMotion ] )

	const sceneClass: string = [
		'Frame Scene Traceability-Scene',
		`is-stage-${ stage }`,
		bFading ? 'is-fading' : '',
		bSnap ? 'is-snap' : '',
	].filter( Boolean ).join( ' ' )

	return (
		<div className="Pane-Light Traceability" id="trace" >
			<div className="Delimiter" />
			<div className="Container" >

				<div className="Section-Head" data-fade >
					<div className="Section-Kicker" >Traceability</div>
					<div className="Title text-shadow-2" >Find out where a copy came from.</div>
				</div>

				{ /* The scene and its captions together: on a phone the captions
				     pin to the bottom of the screen for as long as this box is
				     in view, and no longer (App.css, .Steps-Strip). */ }
				<div className="Scene-Stage" >

				<div className={ sceneClass } ref={ rootRef } data-fade data-fade-delay="0.1" >
					<svg
						className="Scene-Svg"
						viewBox={ `0 0 ${ layout.width } ${ layout.height }` }
						role="img"
						aria-label="Your server sends a marked copy of an image to each of three fans. One copy is found online, is handed back to the server, and the mark says which fan it was given to."
					>
						<defs>
							{ /* The mark, as something the eye can be shown: a fine
							     purple grain over the picture. */ }
							<pattern id="Traceability-Dots" width="6" height="6" patternUnits="userSpaceOnUse" >
								<circle cx="3" cy="3" r="1" fill="var(--brand-purple-light)" />
							</pattern>
						</defs>

						{ layout.sendPaths.map( ( d: string, i: number ): React.JSX.Element => (
							<path className="Traceability-Send" d={ d } pathLength={ 1 } key={ i } />
						) ) }
						<path className="Traceability-Send Traceability-Send-Hot" d={ layout.sendPaths[ LEAKED ] } pathLength={ 1 } />
						<path className="Traceability-Leak" d={ layout.leakPath } />
						<path className="Traceability-Return" d={ layout.returnPath } pathLength={ 1 } />

						<g className="Traceability-Node" style={ place( layout.server ) } >
							<ServerGlyph labelY={ layout.serverLabelY } />
						</g>

						{ layout.recipients.map( ( p: Point, i: number ): React.JSX.Element => (
							<g
								className={ `Traceability-Node Traceability-Recipient${ i === LEAKED ? ' Traceability-Recipient-Hot' : '' }` }
								style={ place( p ) }
								key={ COPY_LETTERS[ i ] }
							>
								<RecipientGlyph name={ `Fan ${ COPY_LETTERS[ i ] }` } />
							</g>
						) ) }

						<g className="Traceability-Node" style={ place( layout.found ) } >
							<FoundGlyph />
						</g>

						{ /* After the nodes, so a copy sits over the frame it lands in.
						     The leaked one is drawn last, so it is the one seen while
						     the three are still stacked at the start. */ }
						{ [ 0, 2, LEAKED ].map( ( i: number ): React.JSX.Element => (
							<g className="Traceability-Copy" style={ copyStyle( layout, stage, i ) } key={ COPY_LETTERS[ i ] } >
								<CopyGlyph letter={ COPY_LETTERS[ i ] } />
							</g>
						) ) }

						<text className="Traceability-MarkLabel" x={ layout.copyStart.x } y={ layout.copyStart.y + 40 } >invisible mark</text>

						<g className="Traceability-Point" >
							<circle className="Traceability-Point-Halo" r="13" />
							<circle className="Traceability-Point-Core" r="4.5" />
							<animateMotion ref={ motionRef } path={ layout.returnPath } dur="1.3s" begin="indefinite" fill="freeze" />
						</g>

						<g style={ place( layout.answer ) } >
							<g className="Traceability-Answer" >
								<rect x="-66" y="-15" width="132" height="30" rx="15" />
								<text y="4.5" >Given to Fan { COPY_LETTERS[ LEAKED ] }</text>
							</g>
						</g>
					</svg>
				</div>

				<Steps titles={ STEPS } durationsMs={ STAGE_DURATIONS_MS } player={ player } fadeDelay="0.15" />

				</div>

			</div>
		</div>
	)

}
